import {
  Box,
  Container,
  Stack,
  Typography,
  Avatar,
  Collapse,
  Paper,
  Toolbar,
  CircularProgress,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCurrentUser, useCurrentStore, useRedirectToHomePage } from 'hooks'
import { Theme, useTheme } from '@mui/material/styles'
import { KButton, KToggleButton } from 'components/core/KButton'
import { useUserProfile, UpdateStatus } from 'hooks/UserProfileHooks'
import { useRef, useEffect, useState } from 'react'
import { KBottomAppBar } from 'components/core/KBottomAppBar'
import { Background2 } from 'components/backgrounds/Background2'
import { KPhoneField } from 'components/core/KPhoneField'
import { KTextField } from 'components/core/KTextField'
import { useToast } from 'contexts'
import { FullScreenLoading } from 'components/core/FullScreenLoading'
import { AddressList } from 'components/account/AddressList'
import { AddressType } from 'models/Address'
import { CreditCardList } from 'components/account/CreditCardList'

const actionButtonSx = (theme: Theme) => ({
  minWidth: 320,
  height: 52,
  [theme.breakpoints.down('sm')]: {
    minWidth: 160,
    height: 40,
  },
})

export const UserProfilePage = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const redirectToHomePage = useRedirectToHomePage()
  const { currentUser } = useCurrentUser()
  const { store } = useCurrentStore()
  const { showToast } = useToast()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    formData,
    isSaving: isProfileSaving,
    isDirty,
    updateStatus: profileUpdateStatus,
    resetStatus: resetProfileStatus,
    handleInputChange,
    handleSaveChanges,
    saveBillingAddress,
    deleteBillingAddress,
    saveDeliveryAddress,
    deleteDeliveryAddress,
  } = useUserProfile()

  const [activeNav, setActiveNav] = useState(0)
  const prevProfileStatusRef = useRef<UpdateStatus>(UpdateStatus.IDLE)

  const billingAddresses = currentUser?.billingAddresses || []
  const deliveryAddresses = currentUser?.deliveryAddresses || []

  const navigationItems = [
    { label: t('User profile').toUpperCase() },
    {
      label: t('Delivery addresses').toUpperCase(),
    },
    {
      label: t('Billing addresses').toUpperCase(),
    },
    { label: t('Payment info').toUpperCase() },
  ]

  useEffect(() => {
    if (
      profileUpdateStatus === UpdateStatus.SUCCESS &&
      prevProfileStatusRef.current !== UpdateStatus.SUCCESS
    ) {
      showToast(t('Profile updated successfully'), 'success')
      resetProfileStatus()
    } else if (
      profileUpdateStatus === UpdateStatus.ERROR &&
      prevProfileStatusRef.current !== UpdateStatus.ERROR
    ) {
      showToast(t('Failed to update profile. Please try again.'), 'error')
      resetProfileStatus()
    }

    prevProfileStatusRef.current = profileUpdateStatus
  }, [profileUpdateStatus, showToast, t, resetProfileStatus])

  const handleNavItemChange = (index: number) => {
    setActiveNav(index)
  }

  if (!currentUser) {
    return <FullScreenLoading />
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack
        sx={{
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Stack sx={{ paddingY: 2, gap: 1.5 }}>
            <Typography variant="h1" color="primary">
              {t('Account')}
            </Typography>

            <Stack direction="row" gap={2} sx={{ flexWrap: 'wrap' }}>
              {navigationItems.map((item, index) => (
                <KToggleButton
                  key={index}
                  onClick={() => handleNavItemChange(index)}
                  variant={activeNav === index ? 'primary' : 'secondary'}
                  size="small"
                  sx={{
                    minWidth: 132,
                    fontSize: theme.typography.subtitle1.fontSize,
                    fontWeight: activeNav === index ? 600 : 400,
                  }}
                >
                  {item.label}
                </KToggleButton>
              ))}
            </Stack>
          </Stack>
        </Container>

        <Background2
          sx={{
            flexGrow: 1,
            py: 2.5,
            pb: 10,
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              height: '100%',
            }}
          >
            <Collapse in={activeNav === 0}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  p: 4,
                  height: '100%',
                }}
              >
                <Stack spacing={4} sx={{ height: '100%' }}>
                  <Typography variant="h5" sx={{ mb: 3 }}>
                    {t('User profile')}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 3,
                      mb: 4,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: theme.palette.primary.main,
                        fontSize: theme.typography.h2.fontSize,
                      }}
                    >
                      {currentUser.firstName?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ mb: 1 }}>
                        {currentUser.firstName} {currentUser.lastName}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={3}>
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                      <KTextField
                        label={t('First name')}
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange('firstName', e.target.value)
                        }
                        required
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        helperText={
                          formData.firstName && formData.firstName.length > 50
                            ? t('First name must be at most 50 characters')
                            : undefined
                        }
                        error={Boolean(
                          formData.firstName && formData.firstName.length > 50,
                        )}
                      />

                      <KTextField
                        label={t('Last name')}
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange('lastName', e.target.value)
                        }
                        required
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        helperText={
                          formData.lastName && formData.lastName.length > 50
                            ? t('Last name must be at most 50 characters')
                            : undefined
                        }
                        error={Boolean(
                          formData.lastName && formData.lastName.length > 50,
                        )}
                      />
                    </Stack>

                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                      <KTextField
                        label={t('Email')}
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange('email', e.target.value)
                        }
                        disabled={true}
                        fullWidth
                        required
                      />

                      <KPhoneField
                        label={t('Phone')}
                        value={formData.phoneNumber || ''}
                        onChange={(e) =>
                          handleInputChange('phoneNumber', e.target.value)
                        }
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            </Collapse>

            <Collapse in={activeNav === 1}>
              <AddressList
                addresses={deliveryAddresses}
                addressType={AddressType.Delivery}
                onSave={saveDeliveryAddress}
                onDelete={deleteDeliveryAddress}
              />
            </Collapse>
            <Collapse in={activeNav === 2}>
              <AddressList
                addresses={billingAddresses}
                addressType={AddressType.Billing}
                onSave={saveBillingAddress}
                onDelete={deleteBillingAddress}
              />
            </Collapse>
            <Collapse in={activeNav === 3}>
              <CreditCardList />
            </Collapse>
          </Container>
        </Background2>
      </Stack>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 10,
          backgroundColor: 'background.paper',
        }}
      >
        <KBottomAppBar direction={isMobile ? 'column' : 'row'}>
          <Toolbar disableGutters>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              sx={{
                width: isMobile ? '100%' : 'auto',
                marginLeft: isMobile ? 0 : 'auto',
                py: isMobile ? 2 : 0,
              }}
            >
              <KButton
                variant="secondary"
                onClick={() => {
                  if (store) {
                    redirectToHomePage()
                  }
                }}
                sx={{
                  ...actionButtonSx(theme),
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {t('Back to Homepage')}
              </KButton>
              <KButton
                onClick={handleSaveChanges}
                disabled={isProfileSaving || !isDirty || activeNav !== 0}
                sx={{
                  ...actionButtonSx(theme),
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {isProfileSaving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <span>{t('Saving...')}</span>
                  </Stack>
                ) : (
                  t('Save Changes')
                )}
              </KButton>
            </Stack>
          </Toolbar>
        </KBottomAppBar>
      </Box>
    </Stack>
  )
}
